import { DefaultContext, FetchResult, MutationHookOptions, MutationResult, NoInfer, useMutation } from '@apollo/client';

import {
  evictCache,
  PeriodName,
  UPDATE_ASC_PROJECT_CAPITALIZATION,
  UpdateAscProjectCapitalizationInput,
  UpdateAscProjectCapitalizationMutation,
  UpdateAscProjectCapitalizationMutationVariables,
} from '@neo.tax/shared';

import { AscProject } from './useAscProjects';

type UseUpdateAscProjectCapitalizationProps<TContext = DefaultContext> = {
  context?: TContext;
  ascProject: AscProject;
  fiscalYearId: string;
  periodName: PeriodName;
};

type UpdateCapitalizationOptions = MutationHookOptions<
  NoInfer<UpdateAscProjectCapitalizationMutation>,
  NoInfer<UpdateAscProjectCapitalizationMutationVariables>,
  DefaultContext
>;

type UseUpdateAscProjectCapitalization = [
  (
    input: UpdateAscProjectCapitalizationInput,
    options?: UpdateCapitalizationOptions,
  ) => Promise<FetchResult<UpdateAscProjectCapitalizationMutation>>,
  MutationResult<UpdateAscProjectCapitalizationMutation>,
];

export const useUpdateAscProjectCapitalization = ({
  ascProject,
  context: contextFromInit,
  fiscalYearId,
  periodName,
}: UseUpdateAscProjectCapitalizationProps): UseUpdateAscProjectCapitalization => {
  const [updateCapitalizationMutation, updateCapitalizationMutationResult] = useMutation<
    UpdateAscProjectCapitalizationMutation,
    UpdateAscProjectCapitalizationMutationVariables
  >(UPDATE_ASC_PROJECT_CAPITALIZATION, {
    context: {
      successToast: {
        title: 'Capitalization updated successfully',
      },
      errorToast: {
        title: 'Error updating capitalization',
        description: 'Something unexpected prevented your changes from saving. Please try again.',
      },
      ...contextFromInit,
    },
  });

  const updateAscProjectCapitalization = (
    input: UpdateAscProjectCapitalizationInput,
    options: UpdateCapitalizationOptions = {},
  ): Promise<FetchResult<UpdateAscProjectCapitalizationMutation>> =>
    updateCapitalizationMutation({
      variables: {
        ascProjectId: ascProject.id,
        updateAscProjectCapitalizationInput: input,
        fiscalYearId,
        periodName,
      },
      update: (cache) => {
        evictCache(cache, ['getAscWages', 'getAscWageSummary', 'getAscProject']);
      },
      context: contextFromInit,
      ...options,
    });

  return [updateAscProjectCapitalization, updateCapitalizationMutationResult];
};
